import {
	commitMutation,
	ConnectionHandler,
	type Environment,
	graphql,
	type RecordSourceSelectorProxy,
} from 'relay-runtime';

import type { connectTeamToContainerMutation } from './__generated__/connectTeamToContainerMutation.graphql';
import { LimitExceededError } from './errors';

type ConnectTeamContainerInput = {
	teamId: string;
	id: string;
	key?: string | null;
	name: string;
	icon?: string | null;
	description?: string | null;
};

interface LinkTeamContainerEvents {
	onSuccess: () => void;
	onLinkError: (error: Error) => void;
}

export const connectTeamToContainer = (
	environment: Environment,
	{ teamId, id, key, name, icon, description }: ConnectTeamContainerInput,
	{ onSuccess, onLinkError }: LinkTeamContainerEvents,
	connectionId?: string,
) => {
	return commitMutation<connectTeamToContainerMutation>(environment, {
		// eslint-disable-next-line @atlassian/relay/must-use-inline
		mutation: graphql`
			mutation connectTeamToContainerMutation($teamId: ID!, $containerId: ID!) {
				graphStore @optIn(to: ["GraphStoreMutation"]) {
					createTeamConnectedToContainer(
						input: { relationships: [{ from: $teamId, to: $containerId }] }
					) @optIn(to: ["GraphStoreTeamConnectedToContainer"]) {
						errors {
							message
							extensions {
								statusCode
								errorType
							}
						}
						success
					}
				}
			}
		`,
		variables: {
			teamId: teamId,
			containerId: id,
		},
		onCompleted: (data, errors) => {
			const statusCode =
				data?.graphStore?.createTeamConnectedToContainer?.errors?.[0]?.extensions?.statusCode?.toString() ??
				'';
			const errorMessage =
				data?.graphStore?.createTeamConnectedToContainer?.errors?.[0]?.message ??
				errors?.[0].message;

			const limitExceededError = errorMessage?.includes('Cannot reference more than');

			if (data?.graphStore?.createTeamConnectedToContainer?.success) {
				onSuccess();
			} else if (limitExceededError) {
				onLinkError(new LimitExceededError(statusCode));
				return;
			} else {
				onLinkError(new Error(statusCode));
			}
		},
		onError: (errors) => {
			onLinkError(errors as Error);
		},
		updater: (store: RecordSourceSelectorProxy, data) => {
			if (!connectionId || !data?.graphStore?.createTeamConnectedToContainer?.success) {
				return;
			}

			const connectionRecord = store.get(connectionId);

			if (!connectionRecord) {
				return;
			}

			const node = store.get(teamId) ?? store.create(teamId, 'TeamContainer');
			node.setValue(id ?? '', 'id');
			node.setValue(key ?? '', 'key');
			node.setValue(name ?? '', 'name');
			node.setValue(icon ?? '', 'icon');
			node.setValue(description ?? '', 'description');

			const newEdge = ConnectionHandler.createEdge(
				store,
				connectionRecord,
				node,
				'TeamContainerEdge',
			);

			ConnectionHandler.insertEdgeAfter(connectionRecord, newEdge);
		},
	});
};
